import {
	Box,
	Button,
	Avatar,
	Flex,
	Heading,
	Text,
	Stack,
	Spacer,
	Icon,
	Badge,
	Tooltip,
	useColorModeValue,
} from '@chakra-ui/react';
import { ItemModel } from 'models';
import { Condition } from 'models/types';
import { FcClock } from 'react-icons/fc';
import { BsTrashFill } from 'react-icons/bs';
import { BiPurchaseTagAlt } from 'react-icons/bi';
import { FiEdit } from 'react-icons/fi';
import { MdOutlineMessage } from 'react-icons/md';
import { toDollarFormat } from 'utils/format';
import { getMarketValueTextConfig } from 'utils/activemarket';
import { getCountryNameFromCountryCode } from 'utils/country';
import GrowthBadge from '../../Badge/GrowthBadge';
import ConditionBadge from '../../ConditionBadge/ConditionBadge';
import ImageCarousel from '../../ImageCarousel/ImageCarousel';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';

export enum ItemAskCardSizingConfig {
	CARD_HEIGHT = '80px',
	PANEL_MIN_HEIGHT = '80px',
}

export enum ItemAskCardStylingConfig {
	BORDER_COLOR_LIGHT = '#f1f3f7',
	BORDER_COLOR_DARK = '#3f434c',
}

export interface ItemAskCardProps {
	item: ItemModel;
	askAmount: number;
	askCreatedDays?: number;
	askUpdatedDays?: number;
	condition?: Condition;
	countryCode?: string;
	askImages?: string[];
	accepted?: boolean;
	complete?: boolean;
	updated?: boolean;
	expired?: boolean;
	useAvatar?: boolean;
	showIcons?: boolean;
	userOwnsAsk?: boolean;
	shorten?: boolean;
	onItemNameClick?: (itemId: string, itemName: string) => void;
	onItemArtistNameClick?: (artistId: string, artistName: string) => void;
	onEditClick?: () => void;
	onRemoveClick?: () => void;
	onContactClick?: () => void;
	onBuyClick?: () => void;
}

export default function ItemAskCard({
	item,
	askAmount,
	askCreatedDays,
	askUpdatedDays,
	condition,
	countryCode,
	askImages = [],
	accepted,
	complete,
	expired,
	updated,
	onItemNameClick,
	onItemArtistNameClick,
	onEditClick,
	onRemoveClick,
	onContactClick,
	onBuyClick,
	useAvatar = false,
	showIcons = false,
	userOwnsAsk = false,
	shorten = false,
}: ItemAskCardProps) {
	const marketPrice = item.getEstimatedMarketPrice();
	const isMarketValue = askAmount === marketPrice;

	function renderItemImage() {
		if (useAvatar) {
			return (
				<Avatar
					backgroundColor="white"
					marginRight={4}
					name={item.name}
					src={item.image}
				/>
			);
		}
		const images = [item.image, ...askImages];
		return (
			<Box marginRight={4}>
				<ImageCarousel
					images={images}
					imageWidth={ItemAskCardSizingConfig.CARD_HEIGHT}
					imageHeight={ItemAskCardSizingConfig.CARD_HEIGHT}
				/>
			</Box>
		);
	}

	function renderAskBadges() {
		const badges = [
			<Badge colorScheme="orange" key="ask_label">Ask</Badge>,
		];
		if (condition) {
			badges.push(<ConditionBadge condition={condition} key="ask_condition" />);
		}
		if (expired) {
			badges.push(<Badge colorScheme="red" key="ask_expired">Expired</Badge>);
		} else if (accepted) {
			badges.push(<Badge colorScheme="green" key="ask_accepted">Accepted</Badge>);
		} else if (complete) {
			badges.push(<Badge colorScheme="green" key="ask_complete">Complete</Badge>);
		}
		return <Flex direction="row" gap={2}>{badges}</Flex>;
	}

	function renderIconWithTooltip(icon: JSX.Element, tooltipLabel: string) {
		return (
			<Tooltip label={tooltipLabel}>
				<Flex>
					{icon}
				</Flex>
			</Tooltip>
		);
	}

	function renderIcons() {
		if (!showIcons || complete || accepted || expired) return null;
		if (userOwnsAsk) {
			return (

				<Flex direction="row" justify="flex-end" gap={1}>
					{renderIconWithTooltip((
						<Icon
							_hover={onEditClick ? { color: "blue.500", cursor: "pointer" } : {}}
							as={FiEdit}
							height={4}
							onClick={onEditClick}
						/>
					), 'Edit Ask')}
					{renderIconWithTooltip((
						<Icon
							_hover={onRemoveClick ? { color: "red.500", cursor: "pointer" } : {}}
							as={BsTrashFill}
							height={4}
							onClick={onRemoveClick}
						/>
					), 'Remove Ask')}
				</Flex>
			);
		}
		return (
			<Flex direction="row" justify="flex-end" gap={1}>
				<Button size="xs" leftIcon={<Icon as={MdOutlineMessage} />} onClick={onContactClick}>
					Inquire
				</Button>
				<Button
					size="xs"
					colorScheme="blue"
					leftIcon={<Icon as={BiPurchaseTagAlt} />}
					onClick={onBuyClick}
					disabled={expired}
				>
					Buy
				</Button>
			</Flex>
		);
	}

	function renderBidMarketDataText() {
		const { text, color } = getMarketValueTextConfig(askAmount, marketPrice, true);
		if (isMarketValue) {
			return (
				<Tooltip label="Priced at Fair Market Value">
					<Badge colorScheme="purple">{text}</Badge>
				</Tooltip>
			);
		}
		return (
			<Tooltip label="FMV = Fair Market Value">
				<Text textAlign="right" fontSize="xs" color={color}>{text}</Text>
			</Tooltip>
		);
	}

	function renderAskMarketDataStatistics() {
		const noDataLabel = shorten ? 'No Data' : 'No Market Data';
		if (!marketPrice) {
			return <Badge colorScheme="pink">{noDataLabel}</Badge>;
		}
		return (
			<Stack spacing={1} align="flex-end">
				{!isMarketValue && <GrowthBadge starting={marketPrice} ending={askAmount} inverse />}
				{renderBidMarketDataText()}
			</Stack>
		);
	}

	function renderAskData() {
		return (
			<Flex direction="column" align="flex-end" gap={2}>
				<Heading fontSize="sm">{toDollarFormat(askAmount)}</Heading>
				<Stack spacing={1}>
					{renderAskMarketDataStatistics()}
					{renderIcons()}
				</Stack>
			</Flex>
		);
	}

	function renderAskItemName() {
		return (
			<Heading
				fontSize="sm"
				_hover={onItemNameClick ? { color: 'blue.500', cursor: 'pointer' } : {}}
				onClick={() => onItemNameClick?.(item.id, item.getDisplayName())}
			>
				{item.name}
			</Heading>
		);
	}

	function renderAskArtistName() {
		return (
			<Text
				fontSize="xs"
				_hover={onItemArtistNameClick ? { color: 'blue.500', cursor: 'pointer' } : {}}
				onClick={() => onItemArtistNameClick?.(item.artistId, item.getArtistName())}
			>
				{item.getArtistName()}
			</Text>
		);
	}

	function renderAskCreatedDuration() {
		const durationText = updated
			? askUpdatedDays ? `Updated ${askUpdatedDays} Days Ago` : 'Updated Today'
			: askCreatedDays ? `Created ${askCreatedDays} Days Ago` : 'Created Today';

		return (
			<Flex align="center" gap={1}>
				<Icon as={FcClock} boxSize={3} />
				<Text fontSize="xs">{durationText}</Text>
			</Flex>
		);
	}

	function renderAskCountryCode() {
		if (!countryCode) return null;
		return (
			<Tooltip label={getCountryNameFromCountryCode(countryCode)}>
				<Flex align="center" gap={1} width={'fit-content'}>
					<Text fontSize="md">{getUnicodeFlagIcon(countryCode)}</Text>
				</Flex>
			</Tooltip>
		);
	}

	return (
		<Box
			minHeight={ItemAskCardSizingConfig.CARD_HEIGHT}
			width="100%"
			padding={2}
			border="1px solid"
			borderColor={useColorModeValue(
				ItemAskCardStylingConfig.BORDER_COLOR_LIGHT,
				ItemAskCardStylingConfig.BORDER_COLOR_DARK
			)}
		>
			<Flex align="center">
				{renderItemImage()}
				<Flex direction="column" textAlign="left" gap={2} pr={shorten ? 0 : { base: 2, md: 4 }}>
					{renderAskItemName()}
					<Stack spacing={1}>
						{renderAskArtistName()}
						<Stack spacing={0}>
							{renderAskCreatedDuration()}
							{renderAskCountryCode()}
						</Stack>
						{renderAskBadges()}
					</Stack>
				</Flex>
				<Spacer />
				{renderAskData()}
			</Flex>
		</Box>
	);
}
